import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Eyebrow,
  FormField,
  Input,
  PageHeader,
} from '@/shared/components';
import { useApi, toApiError, type UpdateTaxSettingsRequest } from '@/shared/api';

/**
 * MwSt-Sätze für den ganzen Workspace. Ersetzt die abgeschaffte „Rechnungs-Stammdaten"-Seite —
 * Absenderdaten leben in /settings/invoice-templates.
 */
export function TaxSettingsPage() {
  const api = useApi();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['taxSettings'],
    queryFn: () => api.taxSettings.get(),
  });

  const [form, setForm] = useState<UpdateTaxSettingsRequest>({
    taxRateAccommodation: 7,
    taxRateServices: 19,
  });

  useEffect(() => {
    if (data) {
      setForm({
        taxRateAccommodation: data.taxRateAccommodation,
        taxRateServices: data.taxRateServices,
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.taxSettings.update(form),
    onSuccess: () => {
      toast.success('Steuersätze gespeichert');
      void qc.invalidateQueries({ queryKey: ['taxSettings'] });
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  if (isLoading) return <p className="text-ink-soft text-sm">Steuersätze werden geladen…</p>;
  if (error || !data) return <EmptyState icon="Error" title="Konnte Steuersätze nicht laden" />;

  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Steuer und"
        titleAccent="MwSt"
        description="Die MwSt-Sätze gelten für alle Rechnungen dieses Workspace. Pro Posten wählen Sie im Editor die Kategorie: Übernachtung, Service oder steuerfrei."
      />

      <Card>
        <CardBody className="p-5">
          <Eyebrow variant="muted">Steuersätze</Eyebrow>
          <p className="text-ink-soft mt-1 text-sm">
            Brutto-Beträge auf Rechnungen werden mit diesen Sätzen in Netto und MwSt
            aufgeschlüsselt.
          </p>
          <div className="mt-4 grid gap-4 sm:max-w-md sm:grid-cols-2">
            <PercentField
              label="Übernachtung"
              value={form.taxRateAccommodation}
              onChange={(v) => setForm((f) => ({ ...f, taxRateAccommodation: v }))}
            />
            <PercentField
              label="Service"
              value={form.taxRateServices}
              onChange={(v) => setForm((f) => ({ ...f, taxRateServices: v }))}
            />
          </div>
        </CardBody>
      </Card>

      <div className="mt-4 flex justify-end">
        <Button variant="brand" onClick={() => save.mutate()} loading={save.isPending}>
          Speichern
        </Button>
      </div>
    </>
  );
}

function PercentField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <FormField label={label} hint="Angabe in Prozent">
      <Input
        type="number"
        inputMode="decimal"
        min={0}
        max={30}
        step={0.1}
        value={value}
        onChange={(e) => {
          const parsed = Number.parseFloat(e.target.value);
          onChange(Number.isFinite(parsed) ? parsed : 0);
        }}
      />
    </FormField>
  );
}
